import { b64enc, b64dec } from './bytes.js';

const BASE = '/api/v1';

export type CreateLockInput = {
  policy: 'manual' | 'time_manual';
  ownerPubkey: Uint8Array;
  unlockUnix?: number;
};

export type CreateLockResult = {
  lockIdHex: string;
  releasePubkey: Uint8Array;
};

export type LockInfo = {
  lock_id: string;
  policy: 'manual' | 'time_manual';
  unlock_unix: number | null;
  released: boolean;
};

async function expectOk<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { /* ignore */ }
    const msg = (body && typeof body === 'object' && 'error' in body) ? String((body as any).error) : `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

export async function createLock(input: CreateLockInput): Promise<CreateLockResult> {
  const body: Record<string, unknown> = {
    policy: input.policy,
    owner_pubkey: b64enc(input.ownerPubkey),
  };
  if (input.unlockUnix !== undefined) body.unlock_unix = input.unlockUnix;

  const res = await fetch(`${BASE}/locks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await expectOk<{ lock_id: string; release_pubkey: string }>(res);
  return {
    lockIdHex: json.lock_id,
    releasePubkey: b64dec(json.release_pubkey),
  };
}

export async function getLock(lockId: string): Promise<LockInfo | null> {
  const res = await fetch(`${BASE}/locks/${encodeURIComponent(lockId)}`);
  if (res.status === 404) return null;
  return expectOk<LockInfo>(res);
}

/**
 * Fetch the X25519 release private key for a lock that has already been released.
 *
 * Returns null if the lock is not yet released, is unknown, or the request
 * failed for any other reason. The backend collapses all of these into a
 * uniform 400 (no enumeration leak), so the caller can't distinguish — and
 * shouldn't need to: in every case, the answer is "no key available right now".
 *
 * Used on import (proactively, in case the lock was released in a prior session)
 * and on SSE release events (in case the lock was released during this session
 * by another browser tab — the SSE event itself doesn't carry the key).
 */
export async function getReleaseKey(lockId: string): Promise<Uint8Array | null> {
  try {
    const res = await fetch(`${BASE}/locks/${encodeURIComponent(lockId)}/release-key`);
    if (!res.ok) return null;
    const json = (await res.json()) as { release_privkey: string };
    return b64dec(json.release_privkey);
  } catch {
    return null;
  }
}

export async function requestChallenge(lockId: string): Promise<{ nonce: Uint8Array; expiresAt: number }> {
  const res = await fetch(`${BASE}/locks/${encodeURIComponent(lockId)}/challenge`, {
    method: 'POST',
  });
  const json = await expectOk<{ nonce: string; expires_at: number }>(res);
  return { nonce: b64dec(json.nonce), expiresAt: json.expires_at };
}

export async function submitRelease(
  lockId: string,
  nonce: Uint8Array,
  signature: Uint8Array,
): Promise<Uint8Array> {
  const res = await fetch(`${BASE}/locks/${encodeURIComponent(lockId)}/release`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      nonce: b64enc(nonce),
      signature: b64enc(signature),
    }),
  });
  const json = await expectOk<{ release_privkey: string }>(res);
  return b64dec(json.release_privkey);
}

export type ReleaseEvent = { released_at: number };

export function watchLock(
  lockId: string,
  onRelease: (e: ReleaseEvent) => void,
): () => void {
  const es = new EventSource(`${BASE}/locks/${encodeURIComponent(lockId)}/watch`);
  es.addEventListener('released', (ev: MessageEvent) => {
    try {
      const data = JSON.parse(ev.data) as ReleaseEvent;
      onRelease(data);
    } catch {
      /* ignore malformed event */
    }
    es.close();
  });
  es.onerror = () => {
    // EventSource auto-reconnects on transient errors; only close if the stream is explicitly done.
    if (es.readyState === EventSource.CLOSED) {
      // no-op
    }
  };
  return () => es.close();
}
